import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getStorageKey } from "@/features/chat/hooks/utils";
import type { ChatMessage, SendChatMessageParams } from "@/features/chat/services/chatService";
import { chatService } from "@/features/chat/services/chatService";

/**
 * Load messages for the selected thread. Returns messages plus optional pending user message
 * for optimistic UI (caller manages pending state).
 */
export function useChatThreadMessages(selectedThreadId: string | null, pendingUserMessage: string | null) {
	const { data: selectedThread } = useQuery({
		queryKey: ["chatThread", selectedThreadId],
		queryFn: () => {
			if (!selectedThreadId) throw new Error("No thread");
			return chatService.getThread(selectedThreadId);
		},
		enabled: !!selectedThreadId,
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		staleTime: 0,
		retry: false,
	});

	const messages: ChatMessage[] = selectedThread?.messages ?? [];
	const displayMessages: ChatMessage[] = pendingUserMessage
		? [
				...messages,
				{
					id: "pending",
					threadId: selectedThreadId ?? "",
					role: "user" as const,
					content: pendingUserMessage,
					metadata: {},
					createdAt: new Date().toISOString(),
				},
			]
		: messages;

	return { messages, displayMessages };
}

/**
 * Send message mutation (through langgraph). On success: clear pending, update selection
 * if backend returns threadId, invalidate thread and list, optionally start job polling.
 */
export function useChatSendMessage(
	selectedThreadId: string | null,
	setSelectedThreadId: (id: string | null) => void,
	workspaceId: string | null | undefined,
	setPendingUserMessage: (msg: string | null) => void,
	setPollingJobId: (id: string | null) => void,
) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: SendChatMessageParams) => chatService.sendMessage(params),
		onSuccess: (data) => {
			setPendingUserMessage(null);
			const threadId = data.threadId ?? selectedThreadId;
			if (threadId && threadId !== selectedThreadId) {
				setSelectedThreadId(threadId);
				if (typeof window !== "undefined") {
					localStorage.setItem(getStorageKey(workspaceId), threadId);
				}
			}
			if (threadId) {
				queryClient.invalidateQueries({ queryKey: ["chatThread", threadId] });
			}
			queryClient.invalidateQueries({ queryKey: ["chatThreads"] });
			if (data.jobId) setPollingJobId(data.jobId);
		},
		onError: () => {
			setPendingUserMessage(null);
			if (selectedThreadId) {
				queryClient.invalidateQueries({ queryKey: ["chatThread", selectedThreadId] });
			}
		},
	});
}
